import React, { useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import { makeStyles, createMuiTheme, ThemeProvider } from "@material-ui/core/styles";

import { T, FONT_SANS } from "../constants";
import { useUi } from "../ui";
import Panel from "../components/Panel";
import Toast from "../components/Toast";
import useRunnerJobs from "../dashboard/useRunnerJobs";
import { useInstallation } from "../../../components/InstallationContext";
import {
  isConciergePageEnabled,
  VEHICLE_PHOTO_MODES,
} from "../../../lib/conciergeFeature";
import authManager from "../../../lib/authManager";
import { JOB_ACTION } from "../jobActions";
import RunnerQueueTable from "./RunnerQueueTable";
import ShiftToggle from "./ShiftToggle";
import ParkCompletionModal from "./ParkCompletionModal";
import CollectionPhotoModal from "./CollectionPhotoModal";
import BookingQuickView from "../components/BookingQuickView";
import useScannedPickup from "./useScannedPickup";
import useRunnerShiftStatus from "./useRunnerShiftStatus";
import useVehiclePhotoMode from "../dashboard/useVehiclePhotoMode";

const runnerTheme = createMuiTheme({ typography: { fontFamily: FONT_SANS } });

const useStyles = makeStyles({
  root: {
    background: T.bg,
    color: T.fg,
    fontFamily: FONT_SANS,
    // Neutralise the console's 20px main gutter so this page owns its padding.
    margin: -20,
    minHeight: "calc(100% + 40px)",
    padding: 24,
    boxSizing: "border-box",
  },
  inner: { maxWidth: 1500, margin: "0 auto" },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: { fontSize: 20, fontWeight: 700, color: T.fg, margin: 0 },
  right: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  muted: { fontSize: 12, color: T.mutedFg },
  panel: { padding: 0, overflow: "hidden" },
  errorBar: {
    marginBottom: 12,
    borderRadius: 8,
    border: `1px solid rgba(255,59,48,0.4)`,
    background: T.dangerTint,
    padding: "8px 12px",
    fontSize: 12,
    color: T.danger,
  },
  footNote: { marginTop: 10, fontSize: 12, color: T.mutedFg },
});

/*
 * The runner queue: every job on the forecourt in one table, whoever holds it.
 * A runner may hold several at once - one car at a time is a rule about
 * driving, not about what a queue can show.
 */
export default function RunnerApp() {
  const s = useStyles();
  const ui = useUi();
  const { facility, installation } = useInstallation();

  const shiftsEnabled = isConciergePageEnabled(facility, "shifts");
  const vehiclePhotoMode = useVehiclePhotoMode({
    facility,
    brandingKey: installation?.brandingKey,
  });

  const {
    onShift,
    shiftWindow,
    busy: shiftBusy,
    error: shiftError,
    toggle: toggleShift,
  } = useRunnerShiftStatus({ enabled: shiftsEnabled });

  const canWork = !shiftsEnabled || onShift;

  const [toast, setToast] = useState(null);
  /* One job at a time is open in a modal; which one, and which modal. */
  const [parking, setParking] = useState(null);
  const [collecting, setCollecting] = useState(null);
  const [detail, setDetail] = useState(null);

  const {
    jobs,
    mineCount,
    accept,
    acceptScannedPickup,
    handBack,
    advance,
    confirmPark,
    confirmDelivery,
    actionError,
    queueError,
    confirming,
    returning,
    busy,
  } = useRunnerJobs({
    facilityId: facility?.id,
    runnerScoped: true,
    vehiclePhotoMode,
  });

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (vehiclePhotoMode === VEHICLE_PHOTO_MODES.DISABLED) {
      setCollecting(null);
    }
  }, [vehiclePhotoMode]);

  const onAdvance = async (job, step = job.action.step) => {
    const { done } = step;
    const updated = await advance(job);
    if (updated) setToast(`${job.plate}: ${done}.`);
  };

  /* One handler for every button on a row: the resolver already decided which
     of these the runner is being offered, and a row with a way past its primary
     passes that one in. */
  const onAction = (job, action = job.action) => {
    if (action?.key === JOB_ACTION.ACCEPT) {
      if (!canWork) {
        setToast("You're off shift - go online to accept jobs.");
        return;
      }
      accept(job);
      return;
    }

    if (action?.key === JOB_ACTION.CONFIRM_PARKED) {
      setParking(job);
      return;
    }

    if (action?.key === JOB_ACTION.COLLECTION_PHOTOS) {
      setCollecting(job);
      return;
    }

    if (action?.key === JOB_ACTION.HAND_OVER) {
      onConfirmDelivery(job);
      return;
    }

    if (action?.key === JOB_ACTION.STEP) onAdvance(job, action.step);
  };

  const onScan = async (bookingId) => {
    if (!canWork) {
      setToast("You're off shift - go online to accept jobs.");
      return false;
    }

    const session = await acceptScannedPickup(bookingId);
    // Null means the write failed - the error bar already says why.
    if (session) setToast(`${session.plate} is yours - go fetch it.`);

    return Boolean(session);
  };

  useScannedPickup({ ready: Boolean(facility?.id), onScan });

  const onConfirmPark = async ({ lotId, bay, offsiteTicket }) => {
    const parked = await confirmPark(parking, { lotId, bay, offsiteTicket });
    if (parked) {
      setParking(null);
      const ticket = offsiteTicket ? ` on ticket ${offsiteTicket}` : "";
      setToast(`${parked.plate} parked${bay ? ` in ${bay}` : ""}${ticket}.`);
    }
  };

  /* Whatever was shot is already saved against the booking, so going is the
     same tap either way - the photos never gate the trip back. */
  const onGoEnRoute = async () => {
    const updated = await advance(collecting);
    if (updated) {
      setToast(`${collecting.plate}: ${collecting.action.step.done}.`);
      setCollecting(null);
    }
  };

  const onConfirmDelivery = async (job) => {
    const delivered = await confirmDelivery(job);
    if (delivered) setToast(`${job.plate} delivered to the guest.`);
  };

  return (
    <ThemeProvider theme={runnerTheme}>
      <Box className={s.root}>
        <div className={s.inner}>
          <div className={s.titleBar}>
            <h1 className={s.title}>Runner queue</h1>
            <div className={s.right}>
              <span className={s.muted}>{mineCount} mine</span>
              <ShiftToggle
                shiftsEnabled={shiftsEnabled}
                onShift={onShift}
                shiftWindow={shiftWindow}
                busy={shiftBusy}
                onToggle={toggleShift}
              />
            </div>
          </div>

          {(shiftError || actionError || queueError) && (
            <div className={s.errorBar}>
              {shiftError ??
                actionError ??
                "Could not load your queue. Retrying..."}
            </div>
          )}

          <Panel className={s.panel}>
            <RunnerQueueTable
              jobs={jobs}
              disabled={busy || !canWork}
              onAction={onAction}
              onOpen={setDetail}
            />
          </Panel>

          <div className={s.footNote}>
            {authManager.userName ?? "Runner"}, acting as runner.
          </div>
        </div>

        {parking && (
          <ParkCompletionModal
            job={parking}
            facilityId={facility?.id}
            confirming={confirming}
            onConfirm={onConfirmPark}
            onClose={() => setParking(null)}
            vehiclePhotoMode={vehiclePhotoMode}
          />
        )}

        {collecting && (
          <CollectionPhotoModal
            job={collecting}
            facilityId={facility?.id}
            advancing={busy}
            onGo={onGoEnRoute}
            onClose={() => setCollecting(null)}
            vehiclePhotoMode={vehiclePhotoMode}
          />
        )}

        {detail && (
          <BookingQuickView
            row={detail}
            facilityId={facility?.id}
            action={detail.action}
            actionBusy={busy}
            onAction={(job) => {
              setDetail(null);
              onAction(job);
            }}
            footerLeft={
              detail.mine && (
                <button
                  type="button"
                  className={ui.mutedBtn}
                  disabled={returning}
                  onClick={() => handBack(detail).then(() => setDetail(null))}
                >
                  {returning ? "Handing back..." : "Hand back to the queue"}
                </button>
              )
            }
            onClose={() => setDetail(null)}
          />
        )}

        {toast && <Toast>{toast}</Toast>}
      </Box>
    </ThemeProvider>
  );
}
